import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import Axios from "axios";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  LuAlertCircle,
  LuCalendarCheck,
  LuCheck,
  LuCheckCircle2,
  LuClipboardCheck,
  LuListTodo,
  LuLoader2,
  LuPencil,
  LuPlus,
  LuSparkles,
  LuTarget,
  LuTrash2,
  LuX,
} from "react-icons/lu";
import helpers from "../../helpers/general-functions";
import { UiButton, INPUT, TEXTAREA } from "../ui/ui";
import SelectMenu from "../ui/SelectMenu";

const config = require("../../config/config");

// Global styles in Login.css target bare <p> elements (font, size, colour),
// so text in this file uses <div>/<span> to stay on the Tailwind design tokens.

export const MAX_PRIORITIES = 10;

export const CLASSIFICATION_OPTIONS = [
  "Credible Manager Profile",
  "Sought after recognition for Managers (Brand)",
  "Product that changes Behaviour",
  "Organisation Building",
  "Growth with Profitability",
  "Other",
];

const CLASSIFICATION_STYLES = {
  "Credible Manager Profile": "tw-bg-blue-50 tw-text-blue-700 tw-ring-blue-200",
  "Sought after recognition for Managers (Brand)":
    "tw-bg-violet-50 tw-text-violet-700 tw-ring-violet-200",
  "Product that changes Behaviour":
    "tw-bg-emerald-50 tw-text-emerald-700 tw-ring-emerald-200",
  "Organisation Building": "tw-bg-amber-50 tw-text-amber-700 tw-ring-amber-200",
  "Growth with Profitability": "tw-bg-rose-50 tw-text-rose-700 tw-ring-rose-200",
  Other: "tw-bg-slate-100 tw-text-slate-700 tw-ring-slate-200",
};

const CLASSIFICATION_ALIASES = {
  "credible manager profile": "Credible Manager Profile",
  "sought after recognition for managers (brand)":
    "Sought after recognition for Managers (Brand)",
  "product that changes behaviour": "Product that changes Behaviour",
  "product that changes behavior": "Product that changes Behaviour",
  "organisation building": "Organisation Building",
  "organization building": "Organisation Building",
  "growth with profitability": "Growth with Profitability",
  other: "Other",
};

export const normClassification = (v) => {
  const s = String(v || "").trim();
  if (!s) return CLASSIFICATION_OPTIONS[0];
  // If server sent "Other (Something)" treat as "Other"
  if (/^other\s*\(.+\)\s*$/i.test(s)) return "Other";
  return (
    CLASSIFICATION_ALIASES[s.toLowerCase()] ||
    (CLASSIFICATION_OPTIONS.includes(s) ? s : CLASSIFICATION_OPTIONS[0])
  );
};

const normalizeItem = (it) => {
  const classification = normClassification(it.classification);
  return {
    id: it.id,
    actionItem: it.actionItem || "",
    status: it.status || "",
    classification,
    classificationOther:
      classification === "Other" ? it.classificationOther || "" : "",
  };
};

// Monday–Friday range for the week containing `date`
export const getWeekRange = (date) => helpers.getWeekDatesFor(date);

export const formatWeekRange = (startDate, endDate) => {
  const start = moment(startDate);
  const end = moment(endDate);
  if (start.isSame(end, "month")) {
    return `${start.format("DD")} – ${end.format("DD MMM YYYY")}`;
  }
  if (start.isSame(end, "year")) {
    return `${start.format("DD MMM")} – ${end.format("DD MMM YYYY")}`;
  }
  return `${start.format("DD MMM YYYY")} – ${end.format("DD MMM YYYY")}`;
};

// Planning opens on the week's Monday; future weeks can't be planned yet
export const isFutureWeek = (startDate) =>
  moment(startDate).isAfter(moment().startOf("isoWeek"), "day");

// Actuals can be recorded once a week has started
export const isWeekStarted = (startDate) =>
  moment(startDate).isSameOrBefore(moment(), "day");

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchWeekActions = async ({ userId, startDate, endDate }) => {
  const res = await Axios.get(`${config.API_BASE}/user/fetchUserActions`, {
    params: { userId, startDate, endDate },
    headers: authHeaders(),
  });
  const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
  return {
    items: rows.map(normalizeItem),
    accomplishment: res?.data?.accomplishment || "",
  };
};

const isServerId = (id) => typeof id === "number" || /^\d+$/.test(String(id));

const CLASSIFICATION_DOTS = {
  "Credible Manager Profile": "tw-bg-blue-500",
  "Sought after recognition for Managers (Brand)": "tw-bg-violet-500",
  "Product that changes Behaviour": "tw-bg-emerald-500",
  "Organisation Building": "tw-bg-amber-500",
  "Growth with Profitability": "tw-bg-rose-500",
  Other: "tw-bg-slate-400",
};

const ClassificationSelect = ({ value, onChange, className = "" }) => (
  <SelectMenu
    fullWidth
    className={className}
    value={value}
    onChange={onChange}
    ariaLabel="Classification"
    options={CLASSIFICATION_OPTIONS.map((opt) => ({
      value: opt,
      label: opt,
      leading: (
        <span
          className={`tw-h-2.5 tw-w-2.5 tw-shrink-0 tw-rounded-full ${CLASSIFICATION_DOTS[opt]}`}
          aria-hidden="true"
        />
      ),
    }))}
  />
);

export const ClassificationPill = ({ classification, other, className = "" }) => {
  const value = normClassification(classification);
  const label = value === "Other" && other ? `Other · ${other}` : value;
  return (
    <span
      className={`tw-inline-flex tw-max-w-full tw-items-center tw-truncate tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-leading-4 tw-ring-1 tw-ring-inset ${CLASSIFICATION_STYLES[value]} ${className}`}
      title={label}
    >
      {label}
    </span>
  );
};

const NumberBadge = ({ n, done = false }) => (
  <span
    className={`tw-mt-0.5 tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-xs tw-font-semibold ${
      done
        ? "tw-bg-emerald-100 tw-text-emerald-700"
        : "tw-bg-primary/10 tw-text-primary"
    }`}
  >
    {done ? <LuCheck aria-hidden="true" /> : n}
  </span>
);

const ProgressBar = ({ value, total }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div
      className="tw-h-1.5 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-secondary"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={total}
    >
      <div
        className={`tw-h-full tw-rounded-full tw-transition-all ${
          pct === 100 ? "tw-bg-success" : "tw-bg-gradient-primary"
        }`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

export const ModalShell = ({
  show,
  onHide,
  icon: Icon,
  title,
  subtitle,
  children,
  footer,
  busy = false,
}) => (
  <Modal
    show={show}
    onHide={busy ? () => {} : onHide}
    size="lg"
    className="weeklyModal"
    backdrop={busy ? "static" : true}
  >
    <div className="tw-font-sans tw-text-foreground">
      <div className="tw-flex tw-items-start tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-border tw-px-6 tw-py-5">
        <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary/10 tw-text-lg tw-text-primary">
          <Icon aria-hidden="true" />
        </div>
        <div className="tw-min-w-0 tw-flex-1">
          <h2 className="tw-m-0 tw-text-lg tw-font-semibold tw-leading-7 tw-text-foreground">
            {title}
          </h2>
          <div className="tw-mt-0.5 tw-text-sm tw-text-muted-foreground">
            {subtitle}
          </div>
        </div>
        <UiButton
          variant="ghost"
          size="icon"
          onClick={onHide}
          disabled={busy}
          aria-label="Close"
          className="-tw-mr-2 -tw-mt-1 tw-text-base"
        >
          <LuX aria-hidden="true" />
        </UiButton>
      </div>
      <div className="tw-max-h-[65vh] tw-overflow-y-auto tw-px-6 tw-py-5">
        {children}
      </div>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded-b-lg tw-border-0 tw-border-t tw-border-solid tw-border-border tw-bg-secondary/60 tw-px-6 tw-py-4">
        {footer}
      </div>
    </div>
  </Modal>
);

/* ------------------------------------------------------------------ */
/* Weekly priorities card (dashboard side panel)                       */
/* ------------------------------------------------------------------ */

export const WeeklyPrioritiesCard = ({
  startDate,
  endDate,
  week,
  onEditPlan,
  onUpdateActuals,
}) => {
  const loading = !week || week.status === "loading";
  const failed = week && week.status === "error";
  const items = (week && week.items) || [];
  const done = items.filter((it) => String(it.status || "").trim()).length;
  const canRecordActuals = isWeekStarted(startDate) && items.length > 0;
  const future = isFutureWeek(startDate);
  const isCurrentWeek = moment().isBetween(
    moment(startDate).startOf("day"),
    moment(startDate).add(6, "days").endOf("day"),
    null,
    "[]",
  );

  return (
    <section className="tw-mt-4 tw-rounded-lg tw-border tw-border-solid tw-border-border/60 tw-bg-card tw-font-sans tw-text-card-foreground tw-shadow-soft">
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-p-5 tw-pb-4">
        <div className="tw-flex tw-min-w-0 tw-items-start tw-gap-3">
          <div className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary/10 tw-text-base tw-text-primary">
            <LuTarget aria-hidden="true" />
          </div>
          <div className="tw-min-w-0">
            <h3 className="tw-m-0 tw-text-base tw-font-semibold tw-leading-6 tw-text-foreground">
              Weekly priorities
            </h3>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-text-xs tw-text-muted-foreground">
              <span>{formatWeekRange(startDate, endDate)}</span>
              {isCurrentWeek && (
                <span className="tw-rounded-full tw-bg-primary/10 tw-px-1.5 tw-py-px tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-primary">
                  This week
                </span>
              )}
            </div>
          </div>
        </div>
        {!loading && !failed && items.length > 0 && !future && (
          <UiButton variant="ghost" size="sm" onClick={onEditPlan}>
            <LuPencil aria-hidden="true" />
            Edit plan
          </UiButton>
        )}
      </div>

      {loading && (
        <div className="tw-space-y-2 tw-px-5 tw-pb-5" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="tw-h-12 tw-animate-pulse tw-rounded-md tw-bg-secondary"
            />
          ))}
        </div>
      )}

      {failed && (
        <div className="tw-mx-5 tw-mb-5 tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-red-50 tw-px-3 tw-py-2.5 tw-text-sm tw-text-destructive">
          <LuAlertCircle aria-hidden="true" />
          Couldn't load this week's priorities.
        </div>
      )}

      {!loading && !failed && items.length === 0 && future && (
        <div className="tw-mx-5 tw-mb-5 tw-flex tw-flex-col tw-items-center tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-px-4 tw-py-6 tw-text-center">
          <LuCalendarCheck
            className="tw-mb-2 tw-text-2xl tw-text-muted-foreground"
            aria-hidden="true"
          />
          <div className="tw-text-sm tw-font-medium tw-text-foreground">
            This week hasn't started yet
          </div>
          <div className="tw-mt-0.5 tw-text-xs tw-text-muted-foreground">
            Planning opens on {moment(startDate).format("dddd, D MMM")}.
          </div>
        </div>
      )}

      {!loading && !failed && items.length === 0 && !future && (
        <div className="tw-mx-5 tw-mb-5 tw-flex tw-flex-col tw-items-center tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-px-4 tw-py-6 tw-text-center">
          <LuListTodo
            className="tw-mb-2 tw-text-2xl tw-text-muted-foreground"
            aria-hidden="true"
          />
          <div className="tw-text-sm tw-font-medium tw-text-foreground">
            No priorities set for this week
          </div>
          <div className="tw-mb-3 tw-mt-0.5 tw-text-xs tw-text-muted-foreground">
            Plan your top 3–10 priorities to stay focused.
          </div>
          <UiButton size="sm" onClick={onEditPlan}>
            <LuPlus aria-hidden="true" />
            Add priorities
          </UiButton>
        </div>
      )}

      {!loading && !failed && items.length > 0 && (
        <>
          <div className="tw-px-5 tw-pb-3">
            <div className="tw-mb-1.5 tw-flex tw-items-center tw-justify-between tw-text-xs">
              <span className="tw-font-medium tw-text-muted-foreground">
                Actuals updated
              </span>
              <span className="tw-font-semibold tw-text-foreground">
                {done}/{items.length}
              </span>
            </div>
            <ProgressBar value={done} total={items.length} />
          </div>

          <ol className="tw-m-0 tw-list-none tw-divide-y tw-divide-border tw-border-0 tw-border-t tw-border-solid tw-border-border tw-p-0">
            {items.map((item, index) => {
              const actual = String(item.status || "").trim();
              return (
                <li key={item.id} className="tw-flex tw-gap-3 tw-px-5 tw-py-3">
                  <NumberBadge n={index + 1} done={!!actual} />
                  <div className="tw-min-w-0 tw-flex-1">
                    <div className="tw-break-words tw-text-sm tw-font-medium tw-leading-5 tw-text-foreground">
                      {item.actionItem}
                    </div>
                    <ClassificationPill
                      className="tw-mt-1"
                      classification={item.classification}
                      other={item.classificationOther}
                    />
                    {actual ? (
                      <div className="tw-mt-2 tw-whitespace-pre-line tw-break-words tw-rounded-md tw-bg-secondary tw-px-2.5 tw-py-1.5 tw-text-xs tw-leading-5 tw-text-foreground">
                        <span className="tw-font-semibold tw-text-muted-foreground">
                          Actual:{" "}
                        </span>
                        {actual}
                      </div>
                    ) : (
                      isWeekStarted(startDate) && (
                        <div className="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
                          Actual not updated yet
                        </div>
                      )
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          {week.accomplishment && (
            <div className="tw-mx-5 tw-mb-1 tw-mt-1 tw-flex tw-gap-2 tw-rounded-md tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-xs tw-leading-5 tw-text-amber-900">
              <LuSparkles className="tw-mt-0.5 tw-shrink-0" aria-hidden="true" />
              <span className="tw-whitespace-pre-line tw-break-words">
                {week.accomplishment}
              </span>
            </div>
          )}

          {canRecordActuals && (
            <div className="tw-p-5 tw-pt-3">
              <UiButton
                variant={
                  done < items.length && !isCurrentWeek ? "primary" : "outline"
                }
                className="tw-w-full"
                onClick={onUpdateActuals}
              >
                <LuClipboardCheck aria-hidden="true" />
                {done === items.length ? "Edit actuals" : "Update actuals"}
              </UiButton>
            </div>
          )}
        </>
      )}
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Plan modal: add / edit / delete a week's priorities                 */
/* ------------------------------------------------------------------ */

const EMPTY_DRAFT = {
  actionItem: "",
  classification: CLASSIFICATION_OPTIONS[0],
  classificationOther: "",
};

const draftError = (draft) => {
  if (!draft.actionItem.trim()) return "Enter a priority";
  if (draft.classification === "Other" && !draft.classificationOther.trim()) {
    return "Describe the 'Other' classification";
  }
  return "";
};

const PriorityFields = ({ draft, onChange, onEnter, autoFocus, placeholder }) => (
  <div className="tw-grid tw-gap-2 sm:tw-grid-cols-5">
    <input
      type="text"
      className={`${INPUT} sm:tw-col-span-3`}
      placeholder={placeholder}
      value={draft.actionItem}
      autoFocus={autoFocus}
      maxLength={500}
      onChange={(e) => onChange({ ...draft, actionItem: e.target.value })}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onEnter();
        }
      }}
    />
    <ClassificationSelect
      className="sm:tw-col-span-2"
      value={draft.classification}
      onChange={(classification) =>
        onChange({
          ...draft,
          classification,
          classificationOther:
            classification === "Other" ? draft.classificationOther : "",
        })
      }
    />
    {draft.classification === "Other" && (
      <input
        type="text"
        className={`${INPUT} sm:tw-col-span-5`}
        placeholder="Describe the 'Other' classification"
        value={draft.classificationOther}
        onChange={(e) =>
          onChange({ ...draft, classificationOther: e.target.value })
        }
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onEnter();
          }
        }}
      />
    )}
  </div>
);

const sameItem = (a, b) =>
  a.actionItem.trim() === b.actionItem.trim() &&
  a.classification === b.classification &&
  (a.classification === "Other"
    ? a.classificationOther.trim() === (b.classificationOther || "").trim()
    : true);

export const WeeklyPlanModal = ({
  show,
  onHide,
  userId,
  startDate,
  endDate,
  items,
  onSaved,
}) => {
  const [list, setList] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY_DRAFT);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const original = useMemo(() => (items || []).map(normalizeItem), [items]);

  useEffect(() => {
    if (show) {
      setList(original.map((i) => ({ ...i })));
      setDeletedIds([]);
      setDraft(EMPTY_DRAFT);
      setEditingId(null);
      setEditDraft(EMPTY_DRAFT);
      setError("");
    }
  }, [show, original]);

  const atLimit = list.length >= MAX_PRIORITIES;

  const addDraft = () => {
    const err = draftError(draft);
    if (err) return setError(err);
    if (atLimit) return setError(`You can add up to ${MAX_PRIORITIES} priorities.`);
    setList((prev) => [
      ...prev,
      {
        id: `tmp-${Date.now()}`,
        status: "",
        actionItem: draft.actionItem.trim(),
        classification: draft.classification,
        classificationOther:
          draft.classification === "Other" ? draft.classificationOther.trim() : "",
      },
    ]);
    setDraft(EMPTY_DRAFT);
    setError("");
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditDraft({
      actionItem: item.actionItem,
      classification: item.classification,
      classificationOther: item.classificationOther || "",
    });
    setError("");
  };

  const applyEdit = (currentList) => {
    const err = draftError(editDraft);
    if (err) {
      setError(err);
      return null;
    }
    return currentList.map((it) =>
      it.id === editingId
        ? {
            ...it,
            actionItem: editDraft.actionItem.trim(),
            classification: editDraft.classification,
            classificationOther:
              editDraft.classification === "Other"
                ? editDraft.classificationOther.trim()
                : "",
          }
        : it,
    );
  };

  const saveEdit = () => {
    const next = applyEdit(list);
    if (!next) return;
    setList(next);
    setEditingId(null);
    setError("");
  };

  const removeItem = (id) => {
    setList((prev) => prev.filter((it) => it.id !== id));
    if (isServerId(id)) setDeletedIds((prev) => [...prev, Number(id)]);
    if (editingId === id) setEditingId(null);
  };

  const originalById = useMemo(() => {
    const map = {};
    original.forEach((it) => (map[String(it.id)] = it));
    return map;
  }, [original]);

  const hasPendingDraft = draft.actionItem.trim() !== "";
  const hasChanges =
    deletedIds.length > 0 ||
    hasPendingDraft ||
    editingId !== null ||
    list.some(
      (it) =>
        !isServerId(it.id) ||
        !originalById[String(it.id)] ||
        !sameItem(it, originalById[String(it.id)]),
    );

  const handleSave = async () => {
    if (saving) return;
    let finalList = list;

    // Fold in an open edit and a typed-but-not-added priority so nothing is lost
    if (editingId !== null) {
      finalList = applyEdit(finalList);
      if (!finalList) return;
    }
    if (hasPendingDraft) {
      const err = draftError(draft);
      if (err) return setError(err);
      finalList = [
        ...finalList,
        {
          id: `tmp-${Date.now()}`,
          actionItem: draft.actionItem.trim(),
          classification: draft.classification,
          classificationOther:
            draft.classification === "Other"
              ? draft.classificationOther.trim()
              : "",
        },
      ];
    }
    if (finalList.length > MAX_PRIORITIES) {
      return setError(`You can add up to ${MAX_PRIORITIES} priorities.`);
    }

    const toPayload = (it) => ({
      actionItem: it.actionItem.trim(),
      classification: it.classification,
      classificationOther:
        it.classification === "Other" ? it.classificationOther.trim() : null,
    });

    const payload = {
      userId,
      startDate,
      endDate,
      toInsert: finalList.filter((it) => !isServerId(it.id)).map(toPayload),
      toUpdate: finalList
        .filter(
          (it) =>
            isServerId(it.id) &&
            originalById[String(it.id)] &&
            !sameItem(it, originalById[String(it.id)]),
        )
        .map((it) => ({ id: Number(it.id), ...toPayload(it) })),
      toDelete: deletedIds,
    };

    setSaving(true);
    setError("");
    try {
      const res = await Axios.post(
        `${config.API_BASE}/user/upsertUserActionsBulk`,
        payload,
        { headers: { "Content-Type": "application/json", ...authHeaders() } },
      );
      const data = res?.data || {};
      if (!data.status) {
        const failures = [
          ...(data.failedInserts || []),
          ...(data.failedUpdates || []),
          ...(data.failedDeletes || []),
        ]
          .map((f) => (f.actionItem ? `"${f.actionItem}": ${f.msg}` : f.msg))
          .join("; ");
        setError(failures || data.message || "Failed to save priorities");
        // Some rows may still have saved; refresh so the card reflects reality
        if (onSaved) onSaved();
        return;
      }
      toast.success("Weekly priorities saved");
      if (onSaved) onSaved();
      onHide();
    } catch (e) {
      console.log("API Error:", e);
      setError("Something went wrong while saving. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const count = list.length;

  return (
    <ModalShell
      show={show}
      onHide={onHide}
      busy={saving}
      icon={LuTarget}
      title="What are your key priorities this week?"
      subtitle={
        <>
          Week of {formatWeekRange(startDate, endDate)} · Add your top 3–
          {MAX_PRIORITIES} priorities
        </>
      }
      footer={
        <>
          <div className="tw-text-xs tw-text-muted-foreground">
            <span className="tw-font-semibold tw-text-foreground">{count}</span>{" "}
            of {MAX_PRIORITIES} priorities
            {count > 0 && count < 3 && " · aim for at least 3"}
          </div>
          <div className="tw-flex tw-gap-2">
            <UiButton variant="outline" onClick={onHide} disabled={saving}>
              Cancel
            </UiButton>
            <UiButton onClick={handleSave} disabled={!hasChanges || saving}>
              {saving ? (
                <LuLoader2 className="tw-animate-spin" aria-hidden="true" />
              ) : (
                <LuCheck aria-hidden="true" />
              )}
              {saving ? "Saving…" : "Save priorities"}
            </UiButton>
          </div>
        </>
      }
    >
      {count === 0 && (
        <div className="tw-mb-4 tw-rounded-lg tw-bg-secondary tw-px-4 tw-py-5 tw-text-center tw-text-sm tw-text-muted-foreground">
          No priorities yet. Add your first one below.
        </div>
      )}

      {count > 0 && (
        <ol className="tw-m-0 tw-mb-4 tw-list-none tw-space-y-2 tw-p-0">
          {list.map((item, index) =>
            editingId === item.id ? (
              <li
                key={item.id}
                className="tw-rounded-lg tw-border tw-border-solid tw-border-primary/40 tw-bg-primary/5 tw-p-3 tw-ring-2 tw-ring-primary/10"
              >
                <PriorityFields
                  draft={editDraft}
                  onChange={setEditDraft}
                  onEnter={saveEdit}
                  autoFocus
                />
                <div className="tw-mt-2 tw-flex tw-justify-end tw-gap-2">
                  <UiButton
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingId(null);
                      setError("");
                    }}
                  >
                    Cancel
                  </UiButton>
                  <UiButton size="sm" onClick={saveEdit}>
                    <LuCheck aria-hidden="true" />
                    Done
                  </UiButton>
                </div>
              </li>
            ) : (
              <li
                key={item.id}
                className="tw-group tw-flex tw-items-start tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-white tw-p-3 tw-transition-shadow hover:tw-shadow-soft"
              >
                <NumberBadge n={index + 1} />
                <div className="tw-min-w-0 tw-flex-1">
                  <div className="tw-break-words tw-text-sm tw-font-medium tw-leading-5 tw-text-foreground">
                    {item.actionItem}
                  </div>
                  <ClassificationPill
                    className="tw-mt-1.5"
                    classification={item.classification}
                    other={item.classificationOther}
                  />
                </div>
                <div className="tw-flex tw-shrink-0 tw-gap-1">
                  <UiButton
                    variant="ghost"
                    size="icon"
                    onClick={() => startEdit(item)}
                    aria-label={`Edit "${item.actionItem}"`}
                    title="Edit"
                  >
                    <LuPencil aria-hidden="true" />
                  </UiButton>
                  <UiButton
                    variant="danger"
                    size="icon"
                    onClick={() => removeItem(item.id)}
                    aria-label={`Delete "${item.actionItem}"`}
                    title="Delete"
                  >
                    <LuTrash2 aria-hidden="true" />
                  </UiButton>
                </div>
              </li>
            ),
          )}
        </ol>
      )}

      {atLimit ? (
        <div className="tw-rounded-lg tw-bg-secondary tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-text-muted-foreground">
          You've reached the maximum of {MAX_PRIORITIES} priorities.
        </div>
      ) : (
        <div className="tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-p-3">
          <div className="tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
            Add a priority
          </div>
          <PriorityFields
            draft={draft}
            onChange={setDraft}
            onEnter={addDraft}
            placeholder="e.g. Ship the Q3 attendance report"
          />
          <div className="tw-mt-2 tw-flex tw-justify-end">
            <UiButton
              variant="outline"
              size="sm"
              onClick={addDraft}
              disabled={!draft.actionItem.trim()}
            >
              <LuPlus aria-hidden="true" />
              Add
            </UiButton>
          </div>
        </div>
      )}

      {error && (
        <div
          className="tw-mt-3 tw-flex tw-items-start tw-gap-2 tw-rounded-md tw-bg-red-50 tw-px-3 tw-py-2 tw-text-sm tw-text-destructive"
          role="alert"
        >
          <LuAlertCircle className="tw-mt-0.5 tw-shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </ModalShell>
  );
};

/* ------------------------------------------------------------------ */
/* Actuals modal: record what happened against each priority           */
/* ------------------------------------------------------------------ */

export const WeeklyActualsModal = ({
  show,
  onHide,
  userId,
  startDate,
  endDate,
  items,
  accomplishment,
  onSaved,
}) => {
  const [statuses, setStatuses] = useState({});
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      const next = {};
      (items || []).forEach((it) => (next[it.id] = it.status || ""));
      setStatuses(next);
      setNote(accomplishment || "");
      setSubmitted(false);
    }
  }, [show, items, accomplishment]);

  const list = items || [];
  const filled = list.filter((it) => String(statuses[it.id] || "").trim()).length;

  const handleSave = async () => {
    if (saving) return;
    setSubmitted(true);
    if (filled !== list.length) {
      toast.error("Please add an actual for every priority.");
      return;
    }
    setSaving(true);
    try {
      const res = await Axios.post(
        `${config.API_BASE}/user/updateUserActionsBulk`,
        {
          userId,
          startDate,
          endDate,
          statusUpdates: list.map((it) => ({
            id: it.id,
            status: String(statuses[it.id] || "").trim(),
          })),
          accomplishment: note,
        },
        { headers: { "Content-Type": "application/json", ...authHeaders() } },
      );
      const data = res?.data || {};
      if (data.status && !(data.failedUpdates || []).length) {
        toast.success("Weekly actuals saved");
        if (onSaved) onSaved();
        onHide();
      } else {
        toast.error(data.message || "Some actuals could not be saved");
        if (onSaved) onSaved();
      }
    } catch (e) {
      console.log("API Error:", e);
      toast.error("Something went wrong while saving. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      show={show}
      onHide={onHide}
      busy={saving}
      icon={LuCalendarCheck}
      title="Weekly actuals"
      subtitle={
        <>Week of {formatWeekRange(startDate, endDate)} · What happened with each priority?</>
      }
      footer={
        <>
          <div className="tw-flex tw-min-w-[160px] tw-flex-1 tw-items-center tw-gap-3 sm:tw-max-w-[240px]">
            <ProgressBar value={filled} total={list.length} />
            <span className="tw-whitespace-nowrap tw-text-xs tw-text-muted-foreground">
              <span className="tw-font-semibold tw-text-foreground">{filled}</span>/
              {list.length} updated
            </span>
          </div>
          <div className="tw-flex tw-gap-2">
            <UiButton variant="outline" onClick={onHide} disabled={saving}>
              Cancel
            </UiButton>
            <UiButton onClick={handleSave} disabled={saving || list.length === 0}>
              {saving ? (
                <LuLoader2 className="tw-animate-spin" aria-hidden="true" />
              ) : (
                <LuCheck aria-hidden="true" />
              )}
              {saving ? "Saving…" : "Save actuals"}
            </UiButton>
          </div>
        </>
      }
    >
      {list.length === 0 ? (
        <div className="tw-rounded-lg tw-bg-secondary tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
          No priorities were set for this week, so there are no actuals to
          update.
        </div>
      ) : (
        <ol className="tw-m-0 tw-list-none tw-space-y-3 tw-p-0">
          {list.map((item, index) => {
            const value = statuses[item.id] || "";
            const missing = submitted && !value.trim();
            return (
              <li
                key={item.id}
                className="tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-white tw-p-3"
              >
                <div className="tw-flex tw-items-start tw-gap-3">
                  <NumberBadge n={index + 1} done={!!value.trim()} />
                  <div className="tw-min-w-0 tw-flex-1">
                    <label
                      htmlFor={`actual-${item.id}`}
                      className="tw-m-0 tw-block tw-break-words tw-text-sm tw-font-medium tw-leading-5 tw-text-foreground"
                    >
                      {item.actionItem}
                    </label>
                    <ClassificationPill
                      className="tw-mt-1"
                      classification={item.classification}
                      other={item.classificationOther}
                    />
                    <textarea
                      id={`actual-${item.id}`}
                      rows={2}
                      className={`${TEXTAREA} tw-mt-2 ${
                        missing ? "tw-border-destructive tw-ring-2 tw-ring-destructive/15" : ""
                      }`}
                      placeholder="What was the outcome? e.g. Done, In progress – 70%, Moved to next week…"
                      value={value}
                      onChange={(e) =>
                        setStatuses((prev) => ({
                          ...prev,
                          [item.id]: e.target.value,
                        }))
                      }
                    />
                    {missing && (
                      <div className="tw-mt-1 tw-text-xs tw-text-destructive">
                        Add an actual for this priority
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
          <li className="tw-pt-1">
            <label
              htmlFor="weekly-accomplishment"
              className="tw-m-0 tw-mb-1.5 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-foreground"
            >
              <LuSparkles className="tw-text-amber-500" aria-hidden="true" />
              Any other key accomplishment?
              <span className="tw-font-normal tw-text-muted-foreground">
                (optional)
              </span>
            </label>
            <textarea
              id="weekly-accomplishment"
              rows={3}
              className={TEXTAREA}
              placeholder="Anything else worth calling out this week"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </li>
        </ol>
      )}
      {list.length > 0 && filled === list.length && (
        <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-medium tw-text-success">
          <LuCheckCircle2 aria-hidden="true" />
          All priorities have an actual
        </div>
      )}
    </ModalShell>
  );
};
